import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { AccountNumberTextInput } from "cerulean";
import { Alert, ResponsiveFlex, Select } from "@narmi/design_system";

import * as formAdapters from "../../../../../adapters";
import RoutingNumberInput from "../RoutingNumberInput";
import { useUserFeatures } from "../../../../../../components/contexts/UserFeaturesContext";
import SectionActions from "../../../common/SectionActions";
import SectionError from "../../../common/SectionError";

import type { ACHDestinationFormType } from "byzantine/src/dbbl/businessLogic/entities/recipients";

interface ACHFormProps {
  recipientId?: API.RecipientId;
  initialValues?: Partial<ACHDestinationFormType>;
  // the flow needs ACH and this recipient doesn't have it yet
  showMissingDetailsAlert: boolean;
  // input the user hasn't saved is blocking another section
  showUnsavedError: boolean;
  onSaved: () => void;
  onCancel: () => void;
}

// saves the recipient's ACH details with their own API call
const ACHForm = ({
  recipientId,
  initialValues,
  showMissingDetailsAlert,
  showUnsavedError,
  onSaved,
  onCancel,
}: ACHFormProps) => {
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  // ACH covers both directions only when the FI also has collect payments
  const showSendAndCollectHint = Boolean(
    features?.ach_payments && features?.ach_payments_collect
  );
  const accountTypes = entities.recipients.useAccountTypeOptions();
  const { form, handleSubmit, errorMessage, loading } =
    entities.recipients.useACHDestinationForm(recipientId, initialValues);

  const handleClickSave = () =>
    handleSubmit(() => {
      form.resetForm({ values: form.values });
      onSaved();
      // a failed save shows its field errors or errorMessage
    }).catch(() => {});

  const handleClickCancel = () => {
    form.resetForm();
    onCancel();
  };

  return (
    <ResponsiveFlex gapSize="s">
      {showMissingDetailsAlert ? (
        <Alert kind="warn" isActive icon="alert-triangle" isDismissable={false}>
          <div className="fontSize--s">
            {l10n.getString("ach-payment-ach-details-banner")}
          </div>
        </Alert>
      ) : null}
      <SectionError
        errorMessage={errorMessage}
        showUnsavedError={showUnsavedError}
      />
      {showSendAndCollectHint ? (
        <p className="fontColor--secondary fontSize--s">
          {l10n.getString("recipient-drawer-ach-hint")}
        </p>
      ) : null}
      <AccountNumberTextInput
        label={l10n.getString("labelAccountNumber")}
        {...formAdapters.fieldWithOnChange(form, "accountNumber")}
      />
      <RoutingNumberInput
        label={l10n.getString("label-routing")}
        routingType="ach"
        {...formAdapters.fieldWithOnChange(form, "routingNumber")}
      />
      <Select
        label={l10n.getString("ach-payment-account-type")}
        {...formAdapters.select(form, "accountType")}
      >
        {accountTypes.map((option) => (
          <Select.Item key={option} value={option}>
            {option}
          </Select.Item>
        ))}
      </Select>
      <SectionActions
        testIdSuffix="ach-destination"
        isSaving={loading}
        onCancel={handleClickCancel}
        onSave={handleClickSave}
      />
    </ResponsiveFlex>
  );
};

export default ACHForm;
